import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AppShell } from './components/AppShell.js'
import { CommandPalette } from './components/CommandPalette.js'
import { ToastContainer, ToastProvider } from './components/Toast.js'
import { UpdatePromptToast } from './components/UpdatePromptToast.js'
import { EmptyState } from './components/ui/EmptyState.js'
import { AuthProvider } from './auth/AuthContext.js'
import { LocaleProvider } from './locale/LocaleContext.js'
import { ThemeProvider } from './theme/ThemeContext.js'
import { AuthPage } from './pages/AuthPage.js'
import { LandingPage } from './pages/LandingPage.js'
import { RouteDetailPage } from './pages/RouteDetailPage/index.js'
import { RouteNewPage } from './pages/RouteEditor.js'
import { DiscoverPage } from './pages/DiscoverPage.js'
import { MePage } from './pages/MePage.js'
import { SettingsPage } from './pages/SettingsPage.js'
import { QrLoginLandingPage } from './pages/QrLoginLandingPage.js'
import { LegalPage } from './pages/LegalPage.js'
import { SupportPage } from './pages/SupportPage.js'
import { AdminPage } from './pages/AdminPage.js'
import { ElevationCursorProvider } from './hooks/useElevationCursor.js'
import { BasemapProviderProvider } from './hooks/useBasemapProvider.js'

export function App() {
  return (
    <LocaleProvider>
      <ThemeProvider>
        <ToastProvider>
          <BrowserRouter>
            <AuthProvider>
              <BasemapProviderProvider>
                <CommandPalette />
                <AppShell>
                  <ElevationCursorProvider>
                    <RoutedErrorBoundary>
                      <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/signin" element={<AuthPage mode="signin" />} />
                        <Route path="/signup" element={<Navigate to="/signin" replace />} />
                        <Route path="/admin/*" element={<AdminPage />} />
                        <Route path="/discover" element={<DiscoverPage />} />
                        {/* Back-compat: old /routes browse URL → /discover. */}
                        <Route path="/routes" element={<Navigate to="/discover" replace />} />
                        <Route path="/routes/new" element={<RouteNewPage />} />
                        <Route path="/routes/:id/edit" element={<RouteEditRedirect />} />
                        <Route path="/routes/:id" element={<RouteDetailPage />} />
                        <Route path="/r/:slug" element={<RouteDetailPage />} />
                        <Route path="/me" element={<MePage />} />
                        <Route path="/me/settings" element={<SettingsPage />} />
                        <Route path="/qr-login" element={<QrLoginLandingPage />} />
                        <Route path="/privacy" element={<LegalPage doc="privacy" />} />
                        <Route path="/terms" element={<LegalPage doc="terms" />} />
                        <Route path="/support" element={<SupportPage />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </RoutedErrorBoundary>
                  </ElevationCursorProvider>
                </AppShell>
                <UpdatePromptToast />
                <ToastContainer />
              </BasemapProviderProvider>
            </AuthProvider>
          </BrowserRouter>
        </ToastProvider>
      </ThemeProvider>
    </LocaleProvider>
  )
}

interface ErrorBoundaryState {
  error: Error | null
  lastResetKey: string | undefined
}

interface ErrorBoundaryProps {
  children: ReactNode
  /** When this value changes, the boundary auto-clears any captured error.
   *  We use it to reset on route navigation: a render-time crash on /r/:slug
   *  must not bleed into /routes/new after the user navigates away. See
   *  `wiki/pitfalls/errorboundary-survives-route-change.md`. */
  resetKey?: string
  /** Optional override for the fallback UI. Defaults to the page-level
   *  `ErrorBoundaryFallback`. Component-scoped boundaries (e.g. the one
   *  around `RouteMap` in `CoverStage`) can pass a more compact fallback
   *  so a single failed widget doesn't blank the whole route. */
  renderFallback?: (props: ErrorBoundaryFallbackProps) => ReactNode
  /** Optional label included in the console.error prefix so support
   *  requests can tell map crashes from page-level crashes at a glance. */
  scopeLabel?: string
}

export interface ErrorBoundaryFallbackProps {
  error: Error
  onReset: () => void
}

/** Subscribes to react-router-dom's location and feeds the pathname into
 *  ErrorBoundary's `resetKey`, so navigating away from a crashed route
 *  auto-clears the boundary instead of leaving the user stuck on a stale
 *  failure screen. */
function RoutedErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation()
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
}

// Page-level ErrorBoundary. Without this a render-time exception inside any
// route component would unmount the whole subtree and leave the user staring
// at the bare AppShell — i.e. "the routes page is suddenly blank".
//
// Exported for direct unit testing; the rendered tree uses
// `RoutedErrorBoundary` (above) which subscribes to `useLocation` to feed
// `resetKey`.
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, lastResetKey: undefined }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error }
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKey !== state.lastResetKey) {
      // Drop any captured error when the reset key changes. This lets
      // RoutedErrorBoundary clear stale failures on route navigation
      // without remounting the children tree on every route change.
      return { error: null, lastResetKey: props.resetKey }
    }
    return null
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to dev tools too so support requests can include the trace.
    // The scope label (when provided by a component-scoped boundary)
    // disambiguates map / widget crashes from page-level crashes in the
    // console feed. We also log `error.stack` separately so V8's
    // function-entry collapse on minified bundles still leaves us a hint
    // to chase down with the sourcemap.
    const scope = this.props.scopeLabel ?? 'RouteRender'
    // eslint-disable-next-line no-console
    console.error(`${scope} error:`, error, info.componentStack)
    if (error.stack) {
      // eslint-disable-next-line no-console
      console.error(`${scope} stack:`, error.stack)
    }
    // TODO: Send error + info.componentStack to first-party monitoring once wired.
  }

  override render(): ReactNode {
    if (this.state.error) {
      const fallbackProps: ErrorBoundaryFallbackProps = {
        error: this.state.error,
        onReset: () => this.setState({ error: null }),
      }
      if (this.props.renderFallback) {
        return this.props.renderFallback(fallbackProps)
      }
      return <ErrorBoundaryFallback {...fallbackProps} />
    }
    return this.props.children
  }
}

function ErrorBoundaryFallback({ error, onReset }: ErrorBoundaryFallbackProps) {
  const { t } = useTranslation('common')
  const location = useLocation()
  const title = encodeURIComponent(`[bug] frontend crash on ${location.pathname}`)
  const reportUrl = `https://github.com/ww4yne/XingRoute/issues/new?title=${title}`

  return (
    <div
      role="alert"
      className="space-y-3 rounded-[var(--xr-radius-lg)] border border-[var(--xr-danger)] bg-[var(--xr-danger-soft)] p-4 text-sm text-[var(--xr-danger)]"
    >
      <div className="space-y-1">
        <p className="font-medium">{t('errorBoundaryTitle')}</p>
        <p className="text-[var(--xr-text-secondary)]">{t('errorBoundaryBody')}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="inline-flex h-[26px] items-center rounded-[var(--xr-radius-md)] bg-[var(--xr-danger)] px-2.5 text-[11px] font-medium text-[var(--xr-danger-ink)] hover:opacity-90"
          onClick={onReset}
        >
          {t('errorBoundaryRetry')}
        </button>
        <a
          className="inline-flex h-[26px] items-center rounded-[var(--xr-radius-md)] border border-[var(--xr-danger)] px-2.5 text-[11px] font-medium text-[var(--xr-danger)] hover:bg-[var(--xr-bg-surface)]"
          href={reportUrl}
          target="_blank"
          rel="noreferrer"
        >
          {t('errorBoundaryReport')}
        </a>
      </div>
      {import.meta.env.DEV && (
        <details className="rounded-[var(--xr-radius-md)] border border-[var(--xr-border-muted)] bg-[var(--xr-bg-surface)] p-2 text-xs">
          <summary>Technical details</summary>
          <pre className="mt-2 overflow-auto whitespace-pre-wrap break-all">
            {String(error.stack ?? error.message)}
          </pre>
        </details>
      )}
    </div>
  )
}

function NotFound() {
  const { t } = useTranslation('common')
  return (
    <EmptyState
      variant="not-found"
      title={t('notFound.title')}
      description={t('notFound.description')}
    />
  )
}

function RouteEditRedirect() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  params.set('edit', '1')
  return <Navigate to={`/routes/${id}?${params.toString()}`} replace />
}
